// Country gate: "Where are you visiting from?" market prompt.
// Markup + settings: sections/sm_country-gate.liquid. Reuses ModalDialog (global.js)
// for Esc, backdrop click and focus return. The country list's search filter, keyboard
// handling and live result count mirror Dawn's localization-form.js.
if (!customElements.get('country-gate') && customElements.get('modal-dialog')) {
  const DISMISS_KEY = 'geoPromptDismissed';
  const ENDPOINT = '/browsing_context_suggestions.json';
  const TIMEOUT_MS = 5000;

  // Shown once per browser: closing it or choosing a country is remembered permanently
  // (localStorage, with a 1-year cookie if storage is blocked). ?geo_reset=1 clears it for QA.
  const dismissal = {
    get() {
      try {
        if (localStorage.getItem(DISMISS_KEY) === '1') return true;
      } catch (error) {
        // Storage blocked: fall back to the cookie.
      }
      return document.cookie.split('; ').includes(`${DISMISS_KEY}=1`);
    },
    set() {
      try {
        localStorage.setItem(DISMISS_KEY, '1');
        return;
      } catch (error) {
        // Storage blocked: fall back to the cookie.
      }
      document.cookie = `${DISMISS_KEY}=1; max-age=31536000; path=/; SameSite=Lax`;
    },
    clear() {
      try {
        localStorage.removeItem(DISMISS_KEY);
      } catch (error) {
        // Nothing stored.
      }
      document.cookie = `${DISMISS_KEY}=; max-age=0; path=/; SameSite=Lax`;
    },
  };

  const isCountryCode = (value) => typeof value === 'string' && /^[A-Z]{2}$/.test(value);

  const normalise = (value) =>
    (value || '')
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .trim();

  // Visible, enabled controls only (global.js's trapFocus also counts hidden ones).
  const focusableIn = (container) =>
    Array.from(container.querySelectorAll('button, input, a[href]')).filter(
      (element) => !element.disabled && element.getClientRects().length > 0
    );

  const onIdle = (callback) => {
    const run = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(callback, { timeout: 3000 });
      } else {
        setTimeout(callback, 1500);
      }
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  };

  // Fails quietly: any error, timeout or missing country resolves to null.
  const detectCountry = async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(ENDPOINT, {
        signal: controller.signal,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const data = await response.json();
      const country = data && data.detected_values && data.detected_values.country;
      const code = country && typeof country.handle === 'string' ? country.handle.toUpperCase() : '';
      if (!isCountryCode(code)) return null;
      return { code, name: country.name || '' };
    } catch (error) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  };

  class CountryGate extends customElements.get('modal-dialog') {
    constructor() {
      super();
      this.dialog = this.querySelector('[role="dialog"]');
      this.form = this.querySelector('form');
      this.toggle = this.querySelector('[data-country-gate-toggle]');
      this.list = this.querySelector('[data-country-gate-list]');
      this.search = this.querySelector('[data-country-gate-search]');
      this.resetButton = this.querySelector('[data-country-gate-reset]');
      this.searchIcon = this.querySelector('[data-country-gate-search-icon]');
      this.status = this.querySelector('[data-country-gate-status]');
      this.results = this.querySelector('[data-country-gate-results]');
      this.featured = this.querySelector('[data-country-gate-featured]');
      this.spacer = this.querySelector('[data-country-gate-spacer]');
      this.suggested = this.querySelector('[data-country-gate-suggested]');
      this.suggestedSpacer = this.querySelector('[data-country-gate-suggested-spacer]');
      this.allItems = Array.from(this.querySelectorAll('[data-country-gate-all] [data-country-gate-item]'));
      this.empty = this.querySelector('[data-country-gate-empty]');
      this.allItems.forEach((item) => {
        item.searchText = normalise(item.dataset.search);
        item.searchCodes = normalise(item.dataset.codes).split(' ');
      });

      this.querySelectorAll('[data-country-gate-stay]').forEach((button) =>
        button.addEventListener('click', () => this.hide())
      );
      this.toggle.addEventListener('click', () => this.setListOpen(this.list.hidden));
      this.form.addEventListener('submit', () => dismissal.set());

      this.search.addEventListener('input', () => this.filterCountries());
      this.search.addEventListener('keydown', (event) => {
        // Enter would submit the form with the first country; move into the results instead.
        if (event.key !== 'Enter') return;
        event.preventDefault();
        const [first] = this.visibleOptions();
        if (first) first.focus();
      });
      this.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
      this.resetButton.addEventListener('click', () => {
        this.search.value = '';
        this.filterCountries();
        this.search.focus();
      });

      // The open list covers the dropdown row: a click elsewhere in the dialog or Esc closes just the list.
      this.addEventListener('click', (event) => {
        if (this.list.hidden || event.target.closest('[data-country-gate-list], [data-country-gate-toggle]')) return;
        this.setListOpen(false);
      });
      this.list.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        this.setListOpen(false);
      });
      this.list.addEventListener('keydown', (event) => this.onListKeyDown(event));
      window.addEventListener('resize', () => {
        if (!this.list.hidden) this.fitPanel();
      });
      this.addEventListener('keydown', (event) => this.trapTab(event));
    }

    setListOpen(open) {
      if (this.list.hidden === !open) return;
      this.list.hidden = !open;
      this.toggle.setAttribute('aria-expanded', String(open));
      if (open) {
        this.fitPanel();
        this.results.scrollTop = 0;
        // The list sits over the toggle, so focus moves to the search field.
        this.search.focus();
      } else {
        this.search.value = '';
        this.filterCountries();
        if (this.contains(document.activeElement) || document.activeElement === document.body) this.toggle.focus();
      }
    }

    // The list hangs below the dialog; keep its bottom on screen on short viewports.
    fitPanel() {
      this.list.style.maxHeight = '';
      const available = window.innerHeight - this.list.getBoundingClientRect().top - 20;
      const cssMax = parseFloat(getComputedStyle(this.list).maxHeight);
      if (available < cssMax) this.list.style.maxHeight = `${Math.max(available, 180)}px`;
    }

    filterCountries() {
      const query = normalise(this.search.value);
      let visible = 0;

      this.allItems.forEach((item) => {
        // Country name contains the query (as Dawn), or the query is exactly its country or currency code.
        const match = !query || item.searchText.includes(query) || item.searchCodes.includes(query);
        item.hidden = !match;
        if (match) visible += 1;
      });
      // Like Dawn's popular countries, the suggested and market groups step aside while searching.
      if (this.featured) this.featured.hidden = Boolean(query);
      if (this.spacer) this.spacer.hidden = Boolean(query);
      const hasSuggestion = Boolean(this.suggested && this.suggested.children.length);
      if (this.suggested) this.suggested.hidden = Boolean(query) || !hasSuggestion;
      if (this.suggestedSpacer) this.suggestedSpacer.hidden = Boolean(query) || !hasSuggestion;
      this.empty.hidden = visible > 0;
      this.resetButton.hidden = !query;
      this.searchIcon.hidden = Boolean(query);

      const template = (window.accessibilityStrings && window.accessibilityStrings.countrySelectorSearchCount) || '';
      this.status.textContent = query && template ? template.replace('[count]', visible) : '';
      this.results.scrollTop = 0;
    }

    visibleOptions() {
      return Array.from(this.results.querySelectorAll('.country-gate__option')).filter(
        (option) => option.getClientRects().length > 0
      );
    }

    // Arrow keys move between the search field and the visible countries.
    onListKeyDown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const items = [this.search, ...this.visibleOptions()];
      const index = items.indexOf(document.activeElement);
      if (index === -1) return;
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      items[(index + step + items.length) % items.length].focus();
    }

    // Keeps Tab inside the dialog. Stops the event so global.js's trap (which counts hidden elements) doesn't run too.
    trapTab(event) {
      if (event.key !== 'Tab' || !this.hasAttribute('open')) return;
      event.stopPropagation();
      const items = focusableIn(this.dialog);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === this.dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // ModalDialog's connectedCallback reads the parent section, which this layout-level modal doesn't have.
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.currentCountry = this.dataset.currentCountry;
      this.auto = this.dataset.auto === 'true';
      this.editorPreview = this.dataset.editorPreview === 'true';
      this.testCountry = this.getTestCountry();

      if (this.hasParam('geo_reset')) {
        dismissal.clear();
        this.dropParam('geo_reset');
      }

      // Automatic prompt off: the gate only opens from the header region picker.
      if (!this.auto) return;

      // No request at all once dismissed (QA overrides and editor preview ignore the flag).
      if (dismissal.get() && !this.testCountry && !this.editorPreview) return;

      onIdle(() => this.init());
    }

    // Detection runs once per page: the automatic prompt and the header picker share the result.
    detect() {
      if (!this.detection) {
        this.detection = this.testCountry ? Promise.resolve({ code: this.testCountry, name: '' }) : detectCountry();
      }
      return this.detection;
    }

    async init() {
      this.countries = this.readCountries();
      this.routing = this.parseRouting(this.dataset.routing || '');

      const detected = await this.detect();
      let match = detected ? this.resolve(detected) : null;
      if (!match && this.editorPreview) match = this.sampleMatch();
      // Skip if the visitor already opened it from the header while detection was running.
      if (!match || this.hasAttribute('open') || this.openedManually) return;

      this.render(match);
      this.loadFlags();
      this.show(document.activeElement);
    }

    // Header region picker: open on demand, with no detection or dismissal check.
    openFromTrigger(trigger) {
      if (this.hasAttribute('open')) return;
      this.openedManually = true;
      if (!this.countries) this.countries = this.readCountries();
      if (!this.routing) this.routing = this.parseRouting(this.dataset.routing || '');
      if (!this.rendered) this.render(null);
      this.loadFlags();
      this.show(trigger);

      // Opened by hand: still put the visitor's detected country at the top once it's known.
      if (!this.suggested || this.suggested.children.length) return;
      this.detect().then((detected) => {
        if (detected) this.showSuggestion(this.destinationFor(detected.code));
      });
    }

    // Copies the suggested country's row to the top of the list. Skipped for the store they're already on.
    showSuggestion(code) {
      if (!this.suggested || !code || code === this.currentCountry) return;
      const source = this.list.querySelector(`[data-country-gate-all] [data-country-gate-item="${code}"]`);
      if (!source) return;
      const item = source.cloneNode(true);
      item.classList.add('country-gate__item--suggested');
      item.removeAttribute('hidden');
      item.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
      this.suggested.replaceChildren(item);
      this.filterCountries();
    }

    // Flags are lazy so they cost nothing when the gate stays closed; load them now it's opening.
    loadFlags() {
      this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    hide() {
      if (!this.hasAttribute('open')) return;
      // Previewing in the theme editor shouldn't count as the merchant dismissing it.
      if (!(window.Shopify && window.Shopify.designMode)) dismissal.set();
      this.setListOpen(false);
      const opener = this.openedBy;
      super.hide();

      // Opened from the menu drawer: Dawn closes the drawer when focus leaves it, so the trigger may now be
      // hidden. Return focus to the drawer's toggle, or restore the drawer's focus trap if it's still open.
      const details = opener && opener.closest && opener.closest('details');
      if (!details) return;
      if (details.hasAttribute('open') && opener.getClientRects().length) {
        trapFocus(details, opener);
      } else {
        const summary = details.querySelector('summary');
        if (summary) summary.focus();
      }
    }

    hasParam(name) {
      try {
        return new URLSearchParams(window.location.search).has(name);
      } catch (error) {
        return false;
      }
    }

    // Removes a one-shot QA param from the address bar and the switch form's return_to, so it isn't replayed.
    dropParam(name) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete(name);
        window.history.replaceState(window.history.state, '', url);
        const returnTo = this.form.querySelector('input[name="return_to"]');
        if (returnTo) {
          const target = new URL(returnTo.value, window.location.origin);
          target.searchParams.delete(name);
          returnTo.value = target.pathname + target.search + target.hash;
        }
      } catch (error) {
        // Leave the URL as it is.
      }
    }

    // ?geo_test=AU forces the detected country on preview/editor, or anywhere when the setting allows it.
    getTestCountry() {
      let code = '';
      try {
        code = (new URLSearchParams(window.location.search).get('geo_test') || '').toUpperCase();
      } catch (error) {
        return '';
      }
      if (!isCountryCode(code)) return '';
      const shopify = window.Shopify || {};
      const isPreview = Boolean(shopify.designMode) || (shopify.theme && shopify.theme.role !== 'main');
      return isPreview || this.dataset.testParam === 'true' ? code : '';
    }

    readCountries() {
      try {
        return JSON.parse(this.querySelector('[data-country-gate-countries]').textContent) || {};
      } catch (error) {
        return {};
      }
    }

    // Lines like "FJ: NZ" send visitors from one country to another country's market.
    parseRouting(text) {
      const routing = {};
      text.split(/[\n,;]+/).forEach((line) => {
        const parts = line.trim().toUpperCase().match(/^([A-Z]{2})\s*[:=>-]+\s*([A-Z]{2})$/);
        if (parts) routing[parts[1]] = parts[2];
      });
      return routing;
    }

    resolve(detected) {
      if (detected.code === this.currentCountry) return null;
      // "Show For Countries / Markets": an empty list means every country.
      const showFor = (this.dataset.showFor || '').split(',').filter(isCountryCode);
      if (showFor.length && !showFor.includes(detected.code)) return null;

      const destination = this.destinationFor(detected.code);
      if (!destination || destination === this.currentCountry) return null;

      return { detected, destination };
    }

    // The country a detected visitor belongs in: their own, a routed one, the fallback, or null.
    destinationFor(code) {
      let destination = this.routing[code] || code;
      if (!this.countries[destination]) {
        if (this.dataset.unmapped !== 'fallback') return null;
        destination = this.dataset.fallback;
        if (!this.countries[destination]) return null;
      }
      return destination;
    }

    // Theme editor preview when the visitor is already on their own market.
    sampleMatch() {
      const code = Object.keys(this.countries).find((country) => country !== this.currentCountry);
      return code ? { detected: { code, name: '' }, destination: code } : null;
    }

    countryName(code, fallbackName) {
      if (this.countries[code]) return this.countries[code];
      try {
        const locale = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en';
        const name = new Intl.DisplayNames([locale], { type: 'region' }).of(code);
        if (name && name !== code) return name;
      } catch (error) {
        // Fall through to the endpoint's English name.
      }
      return fallbackName || code;
    }

    // match is null when opened from the header: no detected or suggested country.
    render(match) {
      const { detected, destination } = match || {};
      const values = {
        country: detected ? this.countryName(detected.code, detected.name) : this.dataset.currentName,
        store: this.dataset.currentName,
        currency: this.dataset.currency,
        destination: destination ? this.countryName(destination) : this.dataset.currentName,
      };
      this.rendered = true;

      this.fill(this.querySelector('[data-country-gate-heading]'), this.dataset.heading, values, false);
      this.fill(this.querySelector('[data-country-gate-body]'), this.dataset.body, values, true);

      if (!match) return;

      // The suggested country is bolded wherever it appears (market group and A-Z list).
      this.list.querySelectorAll(`[data-country-gate-item="${destination}"]`).forEach((item) => {
        item.classList.add('country-gate__item--suggested');
      });
      this.showSuggestion(destination);

      this.dataset.detected = detected.code;
      this.dataset.destination = destination;
    }

    // Replaces [tokens] with their values; in the body they render bold, as in the design.
    fill(element, template, values, bold) {
      element.replaceChildren();
      (template || '').split(/\[(country|store|currency|destination)\]/).forEach((part, index) => {
        if (index % 2 === 0) {
          if (part) element.append(part);
          return;
        }
        const value = values[part] || '';
        if (bold) {
          const strong = document.createElement('strong');
          strong.textContent = value;
          element.append(strong);
        } else {
          element.append(value);
        }
      });
    }
  }

  customElements.define('country-gate', CountryGate);

  // Any [data-country-gate-open] element (the announcement bar region picker) opens the gate.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-country-gate-open]');
    if (!trigger) return;
    const gate = document.querySelector('country-gate');
    if (!gate || typeof gate.openFromTrigger !== 'function') return;
    event.preventDefault();
    gate.openFromTrigger(trigger);
  });

  // Theme editor: selecting the Country Gate section (Footer group) opens the popup, and it reopens after
  // each setting change (the section re-renders), so edits can be previewed.
  if (window.Shopify && window.Shopify.designMode) {
    let selected = false;
    const gateIn = (event) => (event.target && event.target.querySelector ? event.target.querySelector('country-gate') : null);

    document.addEventListener('shopify:section:select', (event) => {
      const gate = gateIn(event);
      if (!gate) return;
      selected = true;
      gate.openFromTrigger(document.activeElement);
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      const gate = gateIn(event);
      if (!gate) return;
      selected = false;
      gate.hide();
    });
    document.addEventListener('shopify:section:unload', (event) => {
      const gate = gateIn(event);
      if (gate && gate.hasAttribute('open')) document.body.classList.remove('overflow-hidden');
    });
    document.addEventListener('shopify:section:load', (event) => {
      const gate = gateIn(event);
      if (gate && selected) gate.openFromTrigger(document.activeElement);
    });
  }
}
